import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from 'react-redux';
import { getCustomerProfiles, selectCustomerProfiles } from '../../redux/features/customerProfileSlice';
import useMercari from "./useMercari"; 
import ProfileCombo from '../../sharedComponents/ProfileCombo';
import {
  Box,
  TextField,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
  Alert,
  Paper,
  Grid,
  Chip,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { get3AMRange } from "../../utils/dateUtils"; // <-- Use get3AMRange
import { toast } from 'react-toastify';

function getToday() {
  const d = new Date();
  return d.toISOString().slice(0, 10);
}

function getStatusMeta(status) {
  if (status === 'done') return { label: 'DONE', color: 'success' };
  if (status === 'lose') return { label: 'LOSE', color: 'error' };
  if (status === 'won') return { label: 'WON — รอกดซื้อ', color: 'error' };
  if (status === 'ended') return { label: 'ENDED — เช็คผล', color: 'warning' };
  return { label: 'PENDING', color: 'default' };
}

// Items registered before this date predate the auto win-detection poller and
// still need the manual Done/Lose buttons (same cutoff as the backend poller).
const AUTO_POLL_START = new Date('2026-07-12T00:00:00.000Z');
const isLegacyItem = (item) => item.createdAt && new Date(item.createdAt) < AUTO_POLL_START;

const MercariAuctionManager = () => {
  // Redux integration for customer profiles
  const dispatch = useDispatch();
  const profiles = useSelector(selectCustomerProfiles);
  const shopName = useSelector((state) => state.shop.name);

  const {
    addAuctionItem,
    updateAuctionItem,
    getAuctionItemsByFinishDate,
    setAuctionItemStatus,
    isLoading,
    isStatusLoading,
    error,
    mercariAccounts,
    getMercariAccounts,
  } = useMercari();

  // State for adding auction item
  const [itemUrl, setItemUrl] = useState("");
  const [smileName, setSmileName] = useState("");
  const [mercariAccountId, setMercariAccountId] = useState("");
  const [resetKey, setResetKey] = useState(0);

  // State for searching auction items
  const [start, setStart] = useState(getToday());
  const [auctionItems, setAuctionItems] = useState([]);

  // State for editing a registered auction item (fix wrong customer / Mercari account)
  const [editItem, setEditItem] = useState(null);
  const [editSmileName, setEditSmileName] = useState("");
  const [editAccountId, setEditAccountId] = useState("");
  const [editSaving, setEditSaving] = useState(false);

  // Add useEffect to fetch customer profiles and mercari accounts
  useEffect(() => {
    dispatch(getCustomerProfiles(shopName));
    getMercariAccounts();
  }, [dispatch, shopName]);

  // Fetch auction items whenever start date changes
  useEffect(() => {
    let ignore = false;
    const fetch = async () => {
      const { start: startISO, end: endISO } = get3AMRange(start);
      const items = await getAuctionItemsByFinishDate({ start: startISO, end: endISO, includeFinished: true });
      if (!ignore) setAuctionItems(items);
    };
    fetch();
    return () => { ignore = true; };
  }, [start]);

  // Handle add auction item — finishTime is fetched from Mercari by the backend.
  // Client-side guards mirror the backend: URL must be a Mercari item link
  // (locale paths like /en/item/m... are fine) and a customer is required.
  const handleAddAuctionItem = async (e) => {
    e.preventDefault();
    if (!/mercari\.com/.test(itemUrl) || !/\/item\/m\d+/.test(itemUrl)) {
      toast.error('ไม่ใช่ลิงก์สินค้า Mercari — เช็คว่าวางลิงก์ถูกช่อง');
      return;
    }
    if (!smileName) {
      toast.error('กรุณาเลือกลูกค้า (Smile Name) ก่อนเพิ่มรายการประมูล');
      return;
    }
    let result = await addAuctionItem({
      itemUrl,
      smileName,
      orderByOptionId: mercariAccountId || null
    });
    if (result?.needsConfirm) {
      // Not an auction *yet* per Mercari (seller may convert later) — admin confirms to add anyway
      if (!window.confirm(result.message)) return;
      result = await addAuctionItem({
        itemUrl,
        smileName,
        orderByOptionId: mercariAccountId || null,
        forceAdd: true
      });
    }
    if (result?.success) {
      if (result.warning) toast.warning(result.warning);
      setItemUrl("");
      setSmileName("");
      setMercariAccountId("");
      setResetKey(prev => prev + 1);
      // Refresh data using get3AMRange
      const { start: startISO, end: endISO } = get3AMRange(start);
      const items = await getAuctionItemsByFinishDate({ start: startISO, end: endISO, includeFinished: true });
      setAuctionItems(items);
    }
  };

  const handleSetStatus = async (id, status) => {
    const success = await setAuctionItemStatus({ id, status });
    if (success) {
      // Refresh data using get3AMRange
      const { start: startISO, end: endISO } = get3AMRange(start);
      const items = await getAuctionItemsByFinishDate({ start: startISO, end: endISO, includeFinished: true });
      setAuctionItems(items);
    }
  };

  const handleCustomerContactChange = (contact) => {
    setSmileName(contact);
  };

  const openEdit = (item) => {
    setEditItem(item);
    setEditSmileName("");
    setEditAccountId(item.orderByOptionId || "");
  };

  const closeEdit = () => {
    setEditItem(null);
    setEditSmileName("");
    setEditAccountId("");
  };

  const handleSaveEdit = async () => {
    if (!editItem) return;
    const accountChanged = (editAccountId || "") !== (editItem.orderByOptionId || "");
    if (!editSmileName && !accountChanged) {
      toast.info('ไม่มีการเปลี่ยนแปลง');
      return;
    }
    setEditSaving(true);
    const result = await updateAuctionItem({
      id: editItem.id,
      smileName: editSmileName || undefined,
      orderByOptionId: editAccountId,
    });
    setEditSaving(false);
    if (result?.success) {
      if (result.warning) toast.warning(result.warning);
      else toast.success('แก้ไขรายการประมูลแล้ว');
      closeEdit();
      const { start: startISO, end: endISO } = get3AMRange(start);
      const items = await getAuctionItemsByFinishDate({ start: startISO, end: endISO, includeFinished: true });
      setAuctionItems(items);
    }
  };

  // Active = still tracked by the poller (null) or resolved-but-unactioned (won/ended —
  // actioned from the queue on the Pending Beam page, shown here read-only).
  const pendingItems = auctionItems.filter((i) => !i.status || i.status === 'won' || i.status === 'ended');
  const finishedItems = auctionItems.filter((i) => i.status === 'done' || i.status === 'lose');

  return (
    <Box sx={{ width: '100%', p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Mercari Auction Manager
      </Typography>
      <Grid container spacing={3}>
        {/* Left: Search and Results (2/3) */}
        <Grid item xs={12} md={8}>
          <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
            <Typography variant="h6" gutterBottom>
              Auction Items by Finish Date
            </Typography>
            <Box
              sx={{
                display: "flex",
                gap: 2,
                alignItems: "center",
                mb: 2,
                flexWrap: "wrap",
              }}
            >
              <TextField
                label="Date"
                type="date"
                value={start}
                onChange={(e) => setStart(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
            </Box>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
              อยู่ระหว่างประมูล
            </Typography>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Item URL</TableCell>
                  <TableCell>Customer Contact</TableCell>
                  <TableCell>Mercari ID</TableCell>
                  <TableCell>Finish Time</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pendingItems.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      No items found.
                    </TableCell>
                  </TableRow>
                ) : (
                  pendingItems.map((item, idx) => (
                    <TableRow key={idx}>
                      <TableCell>
                        <a
                          href={item.itemUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: "#1976d2", textDecoration: "underline" }}
                        >
                          {item.itemUrl}
                        </a>
                      </TableCell>
                      <TableCell>
                        {item.customerContact ? item.customerContact : '-'}
                      </TableCell>
                      <TableCell>
                        {item.orderByLabel ? (
                          <Chip label={item.orderByLabel} size="small" color="primary" variant="outlined" />
                        ) : '-'}
                      </TableCell>
                      <TableCell>
                        {item.finishTime
                          ? new Date(item.finishTime).toLocaleString()
                          : ""}
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={getStatusMeta(item.status).label} color={getStatusMeta(item.status).color} />
                      </TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => openEdit(item)}
                          sx={{ mr: 1, mb: 0.5 }}
                        >
                          แก้ไข
                        </Button>
                        {isLegacyItem(item) ? (
                          <>
                            <Button
                              size="small"
                              variant="contained"
                              color="success"
                              disabled={isStatusLoading}
                              onClick={() => handleSetStatus(item.id, "done")}
                              sx={{ mr: 1 }}
                            >
                              Done
                            </Button>
                            <Button
                              size="small"
                              variant="contained"
                              color="error"
                              disabled={isStatusLoading}
                              onClick={() => handleSetStatus(item.id, "lose")}
                            >
                              Lose
                            </Button>
                          </>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            เช็คอัตโนมัติ — จัดการที่หน้า Pending Beam
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>

            <Divider sx={{ my: 3 }} />

            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
              เรียบร้อยแล้ว (เผื่อมือลั่นกดผิด จะแก้ไข)
            </Typography>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Item URL</TableCell>
                  <TableCell>Customer Contact</TableCell>
                  <TableCell>Mercari ID</TableCell>
                  <TableCell>Finish Time</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {finishedItems.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      No finished items.
                    </TableCell>
                  </TableRow>
                ) : (
                  finishedItems.map((item, idx) => (
                    <TableRow key={`finished-${idx}`}>
                      <TableCell>
                        <a
                          href={item.itemUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: "#1976d2", textDecoration: "underline" }}
                        >
                          {item.itemUrl}
                        </a>
                      </TableCell>
                      <TableCell>{item.customerContact || '-'}</TableCell>
                      <TableCell>
                        {item.orderByLabel ? (
                          <Chip label={item.orderByLabel} size="small" color="primary" variant="outlined" />
                        ) : '-'}
                      </TableCell>
                      <TableCell>
                        {item.finishTime ? new Date(item.finishTime).toLocaleString() : ""}
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={getStatusMeta(item.status).label} color={getStatusMeta(item.status).color} />
                      </TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => openEdit(item)}
                          sx={{ mr: 1, mb: 0.5 }}
                        >
                          แก้ไข
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={isStatusLoading}
                          onClick={() => handleSetStatus(item.id, "pending")}
                          sx={{ mr: 1 }}
                        >
                          Undo
                        </Button>
                        <Button
                          size="small"
                          variant="contained"
                          color="success"
                          disabled={isStatusLoading || item.status === 'done'}
                          onClick={() => handleSetStatus(item.id, "done")}
                          sx={{ mr: 1 }}
                        >
                          Done
                        </Button>
                        <Button
                          size="small"
                          variant="contained"
                          color="error"
                          disabled={isStatusLoading || item.status === 'lose'}
                          onClick={() => handleSetStatus(item.id, "lose")}
                        >
                          Lose
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Paper>
        </Grid>
        {/* Right: Add Auction Item (1/3) */}
        <Grid item xs={12} md={4}>
          <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
            <Typography variant="h6" gutterBottom>
              Add Auction Item
            </Typography>
            <Box
              component="form"
              onSubmit={handleAddAuctionItem}
              sx={{ display: "flex", flexDirection: "column", gap: 2 }}
            >
              <TextField
                label="Item URL"
                value={itemUrl}
                onChange={(e) => setItemUrl(e.target.value)}
                required
                fullWidth
              />
              
              <Box>
                <ProfileCombo
                  key={resetKey}
                  profiles={profiles}
                  onChange={handleCustomerContactChange}
                />
              </Box>

              <FormControl fullWidth size="small">
                <InputLabel>Mercari Account (ID ที่ลงประมูล)</InputLabel>
                <Select
                  value={mercariAccountId}
                  label="Mercari Account (ID ที่ลงประมูล)"
                  onChange={(e) => setMercariAccountId(e.target.value)}
                  disabled={isLoading}
                >
                  <MenuItem value=""><em>ไม่ระบุ</em></MenuItem>
                  {mercariAccounts.map(acc => (
                    <MenuItem key={acc.id} value={acc.id}>{acc.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              <Typography variant="caption" color="text.secondary">
                เวลาจบประมูลดึงจาก Mercari อัตโนมัติ ไม่ต้องกรอก
              </Typography>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={isLoading}
              >
                Add Auction Item
              </Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Edit the CUSTOMER of a registered auction (wrong auction id → cancel + re-create instead) */}
      <Dialog open={!!editItem} onClose={closeEdit} maxWidth="sm" fullWidth>
        <DialogTitle>แก้ไขลูกค้า / Mercari Account ของรายการประมูล</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
            ลิงก์: {editItem?.itemUrl}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            หากลิงก์ผิด ให้ยกเลิกรายการแล้วเพิ่มใหม่ (แก้ที่นี่ได้เฉพาะลูกค้า)
          </Typography>
          <Box>
            <Typography variant="caption" color="text.secondary">
              ลูกค้าปัจจุบัน: {editItem?.customerContact || '-'} — เลือกลูกค้าใหม่ด้านล่าง (เว้นว่างถ้าไม่เปลี่ยน)
            </Typography>
            <ProfileCombo
              profiles={profiles}
              onChange={(contact) => setEditSmileName(contact)}
            />
          </Box>
          <FormControl fullWidth size="small">
            <InputLabel>Mercari Account (ID ที่ลงประมูล)</InputLabel>
            <Select
              value={editAccountId}
              label="Mercari Account (ID ที่ลงประมูล)"
              onChange={(e) => setEditAccountId(e.target.value)}
            >
              <MenuItem value=""><em>ไม่ระบุ</em></MenuItem>
              {mercariAccounts.map(acc => (
                <MenuItem key={acc.id} value={acc.id}>{acc.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEdit} disabled={editSaving}>ยกเลิก</Button>
          <Button onClick={handleSaveEdit} variant="contained" disabled={editSaving}>บันทึก</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MercariAuctionManager;